import React from 'react';
import styled from 'styled-components';
import PropTypes, { shape } from 'prop-types';
import I10n from '../features/i10n';
import DynamicLabel from '../atoms/dynamic-label';
import { possible_starter_selections } from '../constants';
import Configuration from '../models/Configuration';
import IOIsland from '../models/IOIsland';
import MsxIcons from '../features/msx-icons';

const Container = styled.div`
  border-radius: 4px;
  display: flex;
  flex-direction: row;
  color: #626469;
  height: 70px;
  flex-wrap: wrap;

  ${props => !props.isIsland && `
    border: 1px solid #E7E6E6;
  `}

  @media (max-width: 800px) {
    display: none;
  }
`;

const MobileContainer = styled.div`
  display: none;
  color: #626469;
  @media (max-width: 800px) {
    display: block;
    width: 100%;
  }
  margin-bottom: 5px;
`;

const MobileContainerHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  ${props => props.isIsland ?
    `border-top: 1px solid #e7e6e6;
    border-bottom: 1px solid #e7e6e6;`
    : `
    border-left: 1px solid #E7E6E6;
    border-right: 1px solid #E7E6E6;
    border-top: 1px solid #E7E6E6;
    border-bottom: 1px solid #E7E6E6;
    `}
  cursor: pointer;
`;

const MobileContainerConfigurationName = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 800px) {
    flex-direction: column;
    border: none;
  }
`;

const MobileContainerContent = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  overflow: hidden;
  ${props => props.isMobileOpen ? 'max-height: 5000px;' : 'max-height: 0px;'}
  transition: max-height 0.3s;
  ${props => !props.isIsland && `
  border-left: 1px solid #E7E6E6;
  border-right: 1px solid #E7E6E6;
  border-bottom: 1px solid #E7E6E6;
  `}
`;

const MobileContainerPadding = styled.div`
  display: flex;
  @media (max-width: 650px) {
    flex-direction: column;
  }
  flex: 1 1 0px;
  padding: 10px 0px;
  margin: 0px 10px;
`;

const MobileLabelWrapper = styled.div`
  display: flex;
  margin-left:${props => !props.isIsland ? 29 : 0}px;
`;

const MobileIconWrapper = styled.div`
  transition: transform 0.3s;
  transform: ${props => props.isMobileOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  width: 32px;
  height: 32px;
  box-sizing: content-box;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const InformationWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  flex: 1;
  margin-left: 20px;
  padding: 10px 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
      
  @media (max-width: 800px) {
    display: flex;
    flex-direction: row;
    margin-right: 10px;
    margin-bottom: 5px;
    width: 140px;
    border: none;
  }
`;

const FixedInformationWrapper = styled(InformationWrapper)`
  padding: 10px 5px;
  border-right: 1px solid #E7E6E6;
  width: 140px;
  flex: 1;
  justify-content: flex-start;
  margin-left: 20px;

  @media (max-width: 800px) {
    width: auto;
    flex-direction: row;
    border: none;
  }
`;


const LargeFixedInformationWrapper = styled(FixedInformationWrapper)`
  display: flex;
  padding: 10px 5px;
  border-right: 1px solid #E7E6E6;
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const Title = styled.span`
  font-size: 14px;
  font-weight: bold;
  line-height: 12px;
  margin: 0px 5px;
  word-break: break-word;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const InformationLabel = styled.span`
  font-size: 11px;
  line-height: 16px;
  padding: 5px;
  margin-left: ${props => props.isIsland ? 10 : 5 }px;
  margin-right: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
`;

const RowTitle = styled(Row)`
  padding: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowRange = styled(Row)`
  display: flex;
  height: 16px;
  padding: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DynamicLabelWrapper = styled.div`
  margin-left: ${props => props.isIsland ? 10 : 5}px;
`;

const IslandCorner = styled.div`
  border-left: 1px solid #D1D1D1;
  border-bottom: 1px solid #D1D1D1;
  height: 10px;
  width: 15px;
  margin-left: 12px;
  margin-right: 5px;
`;

const LeftContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const RightCompartment = styled.div`
  display: flex;
  flex-direction: column;
`;

class ConfigurationHeader extends React.Component {
  static propTypes = {
    configuration: shape(Configuration).isRequired,
    selectedIOIsland: shape(IOIsland),
    setConfigurationName: PropTypes.func,
    isIsland: PropTypes.bool,
    isLoading: PropTypes.bool,
    renderRangeRules: PropTypes.func
  }

  constructor(props) {
    super(props);
    this.state = {
      isMobileOpen: false
    };
  }

  render = () => {
    const config = this.props.configuration;
    const headModule = config.headModule;
    const isPLCContext = config.starterType === possible_starter_selections.PLC_IO || config.starterType === possible_starter_selections.PAC;
    const selectedIOIsland = this.props.selectedIOIsland;
    const protocols = selectedIOIsland ? [selectedIOIsland.protocol] : (isPLCContext ? headModule.masterProtocols : headModule.slaveProtocols);
    
    return (
      <>
        <MobileContainer isMobileOpen={this.state.isMobileOpen}>
          <MobileContainerHeader 
            isMobileOpen={this.state.isMobileOpen} 
            isIsland={this.props.isIsland} 
            onClick={() => { this.setState({ isMobileOpen: !this.state.isMobileOpen }) }}
          >
            <MobileContainerConfigurationName>
              {!this.props.isIsland && 
                <RowTitle>
                  <MsxIcons name="configuration"></MsxIcons>
                  <Title>{I10n.get('configuration.general')}</Title>
                </RowTitle>
              }
              <MobileLabelWrapper isIsland={this.props.isIsland}>
                {this.props.isIsland && <IslandCorner></IslandCorner>}
                <DynamicLabel
                  value={config.name}
                  setValue={this.props.setConfigurationName}
                  enableEdit={!this.props.isLoading}
                  withCancel={true}
                  customInputProps={{ name: "configurationName", maxLength: "20" }}
                >
                </DynamicLabel>
              </MobileLabelWrapper>
            </MobileContainerConfigurationName>
            <MobileIconWrapper isMobileOpen={this.state.isMobileOpen}>
              <MsxIcons name="down-arrow" size="small"></MsxIcons>
            </MobileIconWrapper>
          </MobileContainerHeader>
          <MobileContainerContent isMobileOpen={this.state.isMobileOpen} isIsland={this.props.isIsland}>
            <MobileContainerPadding isMobileOpen={this.state.isMobileOpen} isIsland={this.props.isIsland}>
              <FixedInformationWrapper id='configuration-header-FiwedInformationWrapper'>
                <LeftContainer>
                  <MsxIcons name="range"></MsxIcons>
                </LeftContainer>
                <RightCompartment>
                  <RowTitle>
                    <Title>{I10n.get('configuration.general.range')}</Title>
                  </RowTitle>
                  <RowRange>
                    <InformationLabel>{I10n.getOrDefault(`ranges.${config.rangeInfo.rangeName}`, config.rangeInfo.rangeName, 'data')}</InformationLabel>
                    {(possible_starter_selections.PAC !== this.props.configuration.starterType) && this.props.renderRangeRules(config.rangeInfo)}
                  </RowRange>
                  <InformationLabel>{headModule.id}</InformationLabel>
                </RightCompartment>
              </FixedInformationWrapper>
              {headModule.powerSupply &&
                <FixedInformationWrapper>
                  <LeftContainer>
                    <MsxIcons name="power-supply"></MsxIcons>
                  </LeftContainer>
                  <RightCompartment>
                    <RowTitle>
                      <Title>{I10n.get('configuration.general.power_supply')}</Title>
                    </RowTitle>
                    <InformationLabel>{headModule.powerSupply}</InformationLabel>
                  </RightCompartment>
                </FixedInformationWrapper>
              }
              {protocols?.length > 0 && 
                <InformationWrapper>
                  <LeftContainer>
                    <MsxIcons name="protocol"></MsxIcons>
                  </LeftContainer>
                  <RightCompartment>
                    <RowTitle>
                      <Title>{I10n.get('configuration.general.embedded_networks')}</Title>
                    </RowTitle>
                    <InformationLabel>{protocols.map(e => e.name).join('; ')}</InformationLabel>
                  </RightCompartment>
                </InformationWrapper>
              }
            </MobileContainerPadding>
          </MobileContainerContent>
        </MobileContainer>
        <Container isIsland={this.props.isIsland}>
          {this.props.isIsland && <IslandCorner></IslandCorner>}
          <LargeFixedInformationWrapper>
            <DynamicLabelWrapper isIsland={this.props.isIsland}>
              <DynamicLabel
                value={config.name}
                setValue={this.props.setConfigurationName}
                enableEdit={!this.props.isLoading}
                withCancel={true}
                customInputProps={{ name: "configurationName", maxLength: "20" }}
              >
              </DynamicLabel>
            </DynamicLabelWrapper>
          </LargeFixedInformationWrapper>
          <FixedInformationWrapper>
            <LeftContainer id='leftContainer'>
              {!this.props.isIsland && <MsxIcons name="range"></MsxIcons>}
            </LeftContainer>
            <RightCompartment id='rightContainer'>
              {!this.props.isIsland && 
                <RowTitle>
                  <Title>{I10n.get('configuration.general.range')}</Title>
                </RowTitle>
              }
              <RowRange>
                <InformationLabel isIsland={this.props.isIsland}>{I10n.getOrDefault(`ranges.${config.rangeInfo.rangeName}`, config.rangeInfo.rangeName, 'data')}</InformationLabel>
                {(possible_starter_selections.PAC !== this.props.configuration.starterType) && this.props.renderRangeRules(config.rangeInfo)}
              </RowRange>
            </RightCompartment>
          </FixedInformationWrapper>
          {!this.props.isIsland && headModule.powerSupply &&
            <FixedInformationWrapper>
              <LeftContainer id='leftContainer'>
                <MsxIcons name="power-supply"></MsxIcons>
              </LeftContainer>
              <RightCompartment id='rightContainer'>
                <RowTitle>
                  <Title>{I10n.get('configuration.general.power_supply')}</Title>
                </RowTitle>
                <RowRange>
                  <InformationLabel isIsland={this.props.isIsland}>{headModule.powerSupply}</InformationLabel>
                </RowRange>
              </RightCompartment>
            </FixedInformationWrapper>
          }
          <InformationWrapper id='configuration-header-InformationWrapper'>
              {!this.props.isIsland && protocols?.length > 0 && <LeftContainer id='leftContainer'><MsxIcons name="protocol"></MsxIcons></LeftContainer>}
            <RightCompartment id='rightContainer'>
              {!this.props.isIsland && protocols?.length > 0 && 
                <RowTitle id='configuration-header-RowTitleInformationWrapper'>
                  <Title>{I10n.get('configuration.general.embedded_networks')}</Title>
                </RowTitle>
              }
              <RowRange id='configuration-header-RowRangeInformationWrapper'>
                <InformationLabel 
                  id='configuration-header-InformationLabel'
                  title={protocols.map(e => e.name).join('; ')}
                  isIsland={this.props.isIsland}>{protocols.map(e => e.name).join('; ')}</InformationLabel>
              </RowRange>
            </RightCompartment>
          </InformationWrapper>
        </Container>
      </>
    );
  }
}

export default ConfigurationHeader;

